import { useState } from 'react';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useQueryClient } from '@tanstack/react-query';
import { mapAuthError, type MappedApiError } from '@mybusinessmate/api-client';
import { OTP_LENGTH } from '@mybusinessmate/schema';
import { AuthHeading } from '../../../../components/auth/auth-heading';
import { BackLink } from '../../../../components/auth/back-link';
import { Banner } from '../../../../components/ui/banner';
import { Button } from '../../../../components/ui/button';
import { Checkbox } from '../../../../components/ui/checkbox';
import { Input } from '../../../../components/ui/input';
import { OtpInput } from '../../../../components/ui/otp-input';
import { textLinkClass } from '../../../../components/ui/text-link';
import { authClient } from '../../../../lib/api';
import { refreshSession } from '../../../../lib/session';

export const Route = createFileRoute('/subdomain/app/_auth/two-factor')({
  component: TwoFactorPage,
});

function TwoFactorPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [useBackup, setUseBackup] = useState(false);
  const [code, setCode] = useState('');
  const [trustDevice, setTrustDevice] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [problem, setProblem] = useState<MappedApiError>();

  const submit = async (value: string) => {
    setSubmitting(true);
    setProblem(undefined);
    const call = useBackup ? authClient.twoFactor.verifyBackupCode : authClient.twoFactor.verifyTotp;
    const { error } = await call({ code: value.trim(), trustDevice });
    const mapped = mapAuthError(error);
    if (mapped) {
      setProblem(mapped);
      setSubmitting(false);
      return;
    }
    await refreshSession(queryClient);
    await navigate({ to: '/subdomain/app' });
  };

  const wrongCode = problem?.kind === 'invalid_code';
  const blocking = problem && !wrongCode ? problem : undefined;
  const ready = useBackup ? code.trim().length > 0 : code.length === OTP_LENGTH;

  return (
    <>
      <BackLink to="/subdomain/app/sign-in/email">Back to sign in</BackLink>
      <AuthHeading
        title="Two-step sign-in"
        subtitle={
          useBackup
            ? 'Enter one of the backup codes you saved when you turned on two-step sign-in.'
            : 'For your security, enter the 6-digit code from your authenticator app.'
        }
      />

      {blocking ? (
        <Banner tone="error" title={blocking.kind === 'two_factor_locked' ? 'Too many wrong codes' : undefined}>
          {blocking.kind === 'two_factor_locked'
            ? 'Two-step sign-in is locked for a short while. Try again in about 15 minutes.'
            : blocking.kind === 'two_factor_expired'
              ? 'Your sign-in session expired. Start again from the sign-in page.'
              : blocking.message}
        </Banner>
      ) : null}

      {useBackup ? (
        <Input
          label="Backup code"
          autoComplete="one-time-code"
          autoCapitalize="none"
          autoFocus
          value={code}
          error={wrongCode ? 'That backup code didn’t work. Check it and try again.' : undefined}
          onChange={(e) => {
            setCode(e.target.value);
            setProblem(undefined);
          }}
        />
      ) : (
        <div className="flex flex-col gap-(--mbm-space-3)">
          <OtpInput
            value={code}
            onChange={(value) => {
              setCode(value);
              setProblem(undefined);
            }}
            onComplete={(value) => void submit(value)}
            disabled={submitting}
            error={wrongCode}
            autoFocus
          />
          {wrongCode ? (
            <p role="alert" className="text-body-small text-feedback-error">
              That code didn’t work. Check it and try again.
            </p>
          ) : null}
        </div>
      )}

      <div className="rounded-(--mbm-radius-md) border border-border-input p-(--mbm-space-4)">
        <Checkbox
          checked={trustDevice}
          onChange={(e) => setTrustDevice(e.target.checked)}
          label={
            <span className="flex flex-col">
              <span className="text-label-medium text-text-primary">Trust this browser</span>
              <span className="text-body-xsmall text-text-tertiary">Skip codes on this computer for 30 days</span>
            </span>
          }
        />
      </div>

      <Button fullWidth disabled={!ready} loading={submitting} onClick={() => void submit(code)}>
        Verify
      </Button>

      <p className="text-center">
        <button
          type="button"
          className={textLinkClass}
          onClick={() => {
            setUseBackup((v) => !v);
            setCode('');
            setProblem(undefined);
          }}
        >
          {useBackup ? 'Use my authenticator app instead' : 'Use a backup code instead'}
        </button>
      </p>
      {problem?.kind === 'two_factor_expired' ? (
        <Link to="/subdomain/app/sign-in/email" className={`${textLinkClass} text-center`}>
          Back to sign in
        </Link>
      ) : null}
    </>
  );
}
